import type { AxiosError } from "axios";
import type { VerifyErrorKind, VerifyErrorResponse } from "./type";

const TOKEN_EXPIRED_CODE = "TOKEN_EXPIRED";
const TOKEN_EXPIRED_MSG_PATTERN = /token\s+expired/i;

/**
 * Normalizes verify API errors for the route.
 * Contract: 401 + code "TOKEN_EXPIRED" = expired; fallback: msg "Token expired".
 * All other 401 (malformed, used, mismatched) = generic invalid.
 */
export function getVerifyErrorKind(error: unknown): VerifyErrorKind {
  const status = getResponseStatus(error);
  const body = getResponseBody(error);

  if (status !== 401) {
    return "invalid";
  }

  if (body?.code === TOKEN_EXPIRED_CODE) {
    return "expired";
  }

  const msg = body?.msg ?? "";
  if (typeof msg === "string" && TOKEN_EXPIRED_MSG_PATTERN.test(msg)) {
    return "expired";
  }

  return "invalid";
}

function getResponseStatus(error: unknown): number | undefined {
  if (error && typeof error === "object" && "response" in error) {
    const response = (error as AxiosError).response;
    return response?.status;
  }
  return undefined;
}

function getResponseBody(error: unknown): VerifyErrorResponse | undefined {
  if (error && typeof error === "object" && "response" in error) {
    const response = (error as AxiosError).response;
    const data = response?.data;
    if (data && typeof data === "object" && "msg" in data) {
      return data as VerifyErrorResponse;
    }
  }
  return undefined;
}
